import { normalizeMinimumStock } from '../../utils/adminPanelHelpers';

export function normalizeNotificationPreferences(value, defaults = {}) {
  if (!value || typeof value !== 'object') {
    return { ...defaults };
  }

  return {
    ...defaults,
    ...value,
  };
}

export function getNotificationKey(type, id) {
  return `${type}_${id}`;
}

export function buildReadNotificationsSnapshot({
  onderdelen = [],
  magazijnVoorraad = [],
  bijzonderheden = [],
  garanties = [],
  tickets = [],
}) {
  // Defensive: ensure callers passing unexpected types don't break .filter calls
  if (!Array.isArray(onderdelen)) onderdelen = [];
  if (!Array.isArray(magazijnVoorraad)) magazijnVoorraad = [];
  if (!Array.isArray(bijzonderheden)) bijzonderheden = [];
  if (!Array.isArray(garanties)) garanties = [];
  const next = {};
  tickets.forEach((ticket) => { next[getNotificationKey('ticket_new', ticket.ticketNumber || ticket.id)] = true; });

  onderdelen
    .filter((item) => (magazijnVoorraad.find((voorraad) => voorraad.id === item.code)?.aantal ?? 0) === 0)
    .forEach((item) => {
      next[getNotificationKey('onderdeel_leeg', item.code)] = true;
    });

  onderdelen
    .filter((item) => {
      const voorraad = magazijnVoorraad.find((voorraadItem) => voorraadItem.id === item.code)?.aantal ?? 0;
      const minimumStock = normalizeMinimumStock(item.minimumStock, 1);
      return voorraad > 0 && voorraad <= minimumStock;
    })
    .forEach((item) => {
      next[getNotificationKey('onderdeel_laag', item.code)] = true;
    });

  bijzonderheden
    .filter((item) => {
      if (!item.expiryDate || !item.active) return false;
      const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry <= 7 && daysUntilExpiry >= 0;
    })
    .forEach((item) => {
      next[getNotificationKey('bijzonderheid_expiring', item.id)] = true;
    });

  garanties
    .filter((item) => {
      if (!item.expiryDate) return false;
      const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);
      const now = new Date();
      const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
      return daysUntilExpiry <= 30 && daysUntilExpiry >= 0;
    })
    .forEach((item) => {
      next[getNotificationKey('garantie_expiring', item.id)] = true;
    });

  return next;
}

export function countActiveNotifications({
  onderdelen = [],
  magazijnVoorraad = [],
  bijzonderheden = [],
  garanties = [],
  tickets = [],
  isRead = () => false,
}) {
  if (!Array.isArray(onderdelen)) onderdelen = [];
  if (!Array.isArray(magazijnVoorraad)) magazijnVoorraad = [];
  if (!Array.isArray(bijzonderheden)) bijzonderheden = [];
  if (!Array.isArray(garanties)) garanties = [];
  const emptyPartsCount = onderdelen.filter((item) => {
    return (magazijnVoorraad.find((voorraad) => voorraad.id === item.code)?.aantal ?? 0) === 0 && !isRead('onderdeel_leeg', item.code);
  }).length;

  const lowStockCount = onderdelen.filter((item) => {
    const voorraad = magazijnVoorraad.find((voorraadItem) => voorraadItem.id === item.code)?.aantal ?? 0;
    const minimumStock = normalizeMinimumStock(item.minimumStock, 1);
    return voorraad > 0 && voorraad <= minimumStock && !isRead('onderdeel_laag', item.code);
  }).length;

  const bijzonderhedenCount = bijzonderheden.filter((item) => {
    if (!item.expiryDate || !item.active) return false;
    const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 7 && daysUntilExpiry >= 0 && !isRead('bijzonderheid_expiring', item.id);
  }).length;

  const garantiesCount = garanties.filter((item) => {
    if (!item.expiryDate) return false;
    const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);
    const now = new Date();
    const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
    return daysUntilExpiry <= 30 && daysUntilExpiry >= 0 && !isRead('garantie_expiring', item.id);
  }).length;

  const ticketsCount = tickets.filter((ticket) => !isRead('ticket_new', ticket.ticketNumber || ticket.id)).length;

  return emptyPartsCount + lowStockCount + bijzonderhedenCount + garantiesCount + ticketsCount;
}

export function buildActiveNotifications({
  onderdelen = [],
  magazijnVoorraad = [],
  bijzonderheden = [],
  garanties = [],
  tickets = [],
  isRead = () => false,
}) {
  if (!Array.isArray(onderdelen)) onderdelen = [];
  if (!Array.isArray(magazijnVoorraad)) magazijnVoorraad = [];
  if (!Array.isArray(bijzonderheden)) bijzonderheden = [];
  if (!Array.isArray(garanties)) garanties = [];
  return [
    ...tickets.filter((ticket) => !isRead('ticket_new', ticket.ticketNumber || ticket.id)).map((ticket) => ({
      id: getNotificationKey('ticket_new', ticket.ticketNumber || ticket.id),
      type: 'ticket_new',
      title: `Nieuw ticket: ${ticket.ticketNumber || ticket.id}`,
      message: [ticket.ticketTypes, ticket.municipality, Array.isArray(ticket.robots) ? ticket.robots.join(', ') : ticket.robots].filter(Boolean).join(' · ') || 'Nieuw ticket ontvangen',
      navigateTo: `/administratie/tickets/${encodeURIComponent(ticket.ticketNumber || ticket.id)}`,
      timestamp: ticket.createdAt || ticket.updatedAt || null,
    })),
    ...onderdelen
      .filter((item) => (magazijnVoorraad.find((voorraad) => voorraad.id === item.code)?.aantal ?? 0) === 0 && !isRead('onderdeel_leeg', item.code))
      .map((item) => ({
        id: getNotificationKey('onderdeel_leeg', item.code),
        type: 'onderdeel_leeg',
        title: `Onderdeel op: ${item.name}`,
        message: 'Geen voorraad meer beschikbaar',
        itemId: item.code,
        navigateTo: `/administratie/onderdelen?onderdeel=${encodeURIComponent(item.code || '')}`,
      })),
    ...onderdelen
      .filter((item) => {
        const voorraad = magazijnVoorraad.find((voorraadItem) => voorraadItem.id === item.code)?.aantal ?? 0;
        const minimumStock = normalizeMinimumStock(item.minimumStock, 1);
        return voorraad > 0 && voorraad <= minimumStock && !isRead('onderdeel_laag', item.code);
      })
      .map((item) => {
        const voorraad = magazijnVoorraad.find((voorraadItem) => voorraadItem.id === item.code)?.aantal ?? 0;
        const minimumStock = normalizeMinimumStock(item.minimumStock, 1);
        return {
          id: getNotificationKey('onderdeel_laag', item.code),
          type: 'onderdeel_laag',
          title: `Lage voorraad: ${item.name}`,
          message: `Nog maar ${voorraad} beschikbaar (minimum: ${minimumStock})`,
          itemId: item.code,
          navigateTo: `/administratie/onderdelen?onderdeel=${encodeURIComponent(item.code || '')}`,
        };
      }),
    ...bijzonderheden
      .filter((item) => {
        if (!item.expiryDate || !item.active) return false;
        const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry <= 7 && daysUntilExpiry >= 0 && !isRead('bijzonderheid_expiring', item.id);
      })
      .map((item) => {
        const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        return {
          id: getNotificationKey('bijzonderheid_expiring', item.id),
          type: 'bijzonderheid_expiring',
          title: `Bijzonderheid verloopt: ${item.title || item.description?.substring(0, 30) + '...'}`,
          message: `Verloopt over ${daysUntilExpiry} dagen`,
          itemId: item.id,
          navigateTo: '/bijzonderheden',
        };
      }),
    ...garanties
      .filter((item) => {
        if (!item.expiryDate) return false;
        const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        return daysUntilExpiry <= 30 && daysUntilExpiry >= 0 && !isRead('garantie_expiring', item.id);
      })
      .map((item) => {
        const expiryDate = item.expiryDate.toDate ? item.expiryDate.toDate() : new Date(item.expiryDate);
        const now = new Date();
        const daysUntilExpiry = Math.ceil((expiryDate - now) / (1000 * 60 * 60 * 24));
        return {
          id: getNotificationKey('garantie_expiring', item.id),
          type: 'garantie_expiring',
          title: `Garantie verloopt: ${item.title || item.description?.substring(0, 30) + '...'}`,
          message: `Verloopt over ${daysUntilExpiry} dagen`,
          itemId: item.id,
          navigateTo: '/bijzonderheden',
        };
      }),
  ];
}
